import {
	Button,
	ButtonGroup,
	Card,
	Classes,
	FormGroup,
	IButtonProps,
	Menu,
	MenuItem,
	Popover,
	Position,
	Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IWidgetProps } from "decorator-form";
import {
	CompositeDecorator,
	ContentState,
	convertFromHTML,
	convertFromRaw,
	convertToRaw,
	DefaultDraftBlockRenderMap,
	EditorState,
	getDefaultKeyBinding,
	KeyBindingUtil,
	Modifier,
	RichUtils,
	AtomicBlockUtils
} from "draft-js";
import React, { KeyboardEvent } from "react";
import { TextAreaWidget } from "common/form/widgets/TextAreaWidget";
import { Editor } from "react-draft-wysiwyg";
import "./editor.css";
import { composeDecorators } from "draft-js-plugins-editor";
import IOption from "common/models/IOption";
import ButtonSelect from "../common/ButtonSelect";
import DropdownSelect from "../common/DropdownSelect";
import createFocusPlugin from "draft-js-focus-plugin";
import createAlignmentPlugin from "draft-js-alignment-plugin";
import { stateToHTML } from "draft-js-export-html";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";

import {
	customChunkRenderer,
	entityMapper,
	customBlockRenderFunc
} from "./helper";
// https://www.pngall.com/wp-content/uploads/8/Sample-PNG-Image.png
/**
 * Rich text editor widget that creates an HTML string.
 *
 * WARNING
 * This does not do any internal sanitization of the content.
 * Be sure to sanitize server side if the content is being
 * saved with a mutation that does not have admin role restrictions.
 *
 * @author chris
 */

const inlineStyleOptions: IOption[] = [
	{ value: "BOLD", label: "Bold", icon: IconNames.BOLD },
	{ value: "ITALIC", label: "Italic", icon: IconNames.ITALIC },
	{ value: "UNDERLINE", label: "Underline", icon: IconNames.UNDERLINE }
];

const blockStyleOptions: IOption[] = [
	{ value: "unstyled", label: "Normal" },
	{ value: "header-one", label: "Heading 1" },
	{ value: "header-two", label: "Heading 2" },
	{ value: "header-three", label: "Heading 3" },
	{ value: "header-four", label: "Heading 4" },
	{ value: "header-five", label: "Heading 5" },
	{ value: "header-six", label: "Heading 6" }
];

const blockTypeOptions: IOption[] = [
	{
		value: "unordered-list-item",
		label: "Unordered List",
		icon: IconNames.PROPERTIES
	},
	{
		value: "ordered-list-item",
		label: "Ordered List",
		icon: IconNames.NUMBERED_LIST
	},
	{ value: "blockquote", label: "Blockquote", icon: IconNames.CITATION }
];

const IS_DRAFT_JS = true;

const decorator = new CompositeDecorator([
	// link
	{
		strategy: (contentBlock, callback, contentState) => {
			contentBlock.findEntityRanges(character => {
				const entityKey = character.getEntity();

				return (
					entityKey !== null &&
					contentState.getEntity(entityKey).getType() === "LINK"
				);
			}, callback);
		},
		component: (props: any) => {
			const { contentState, entityKey } = props;
			const { url } = contentState.getEntity(entityKey).getData();
			return (
				<a href={url} target="_blank" aria-label={url}>
					{props.children}
				</a>
			);
		}
	}

	// TODO template variable
]);

interface IRichTextWidgetProps extends IWidgetProps<string> {
	argNames?: string[];

	// optional appearance modifiers
	minimal?: boolean;
	small?: boolean;
	large?: boolean;
}

interface IRichTextWidgetState {
	editorState: EditorState;
	isOpen: boolean;
	htmlVal?: string;
}

const getHtml = editorState =>
	draftToHtml(convertToRaw(editorState.getCurrentContent()));
export default class RichTextWidgdet extends React.Component<
	IRichTextWidgetProps,
	IRichTextWidgetState
> {
	private timer: any;
	focusPlugin = createFocusPlugin();
	alignmentPlugin = createAlignmentPlugin();

	decorator = composeDecorators(
		this.alignmentPlugin.decorator,
		this.focusPlugin.decorator
	);

	plugins = [this.focusPlugin, this.alignmentPlugin];

	picker: any;
	getEditorState: () => EditorState;
	updateEditorState: (editorState: EditorState) => void;
	editor: Editor;

	public constructor(props: IRichTextWidgetProps) {
		super(props);

		let editorState: EditorState;
		if (props.value && props.value !== "null") {
			if (this.isRaw(props.value)) {
				const DBEditorState = convertFromRaw(
					JSON.parse(this.props.value)
				);
				editorState = EditorState.createWithContent(
					DBEditorState,
					decorator
				);
			} else {
				//to keep <br> tag
				const blockRenderMap = DefaultDraftBlockRenderMap.set("br", {
					element: "br"
				});
				// const fromHtml = htmlToDraft(
				// 	props.value,
				// 	undefined,
				// 	blockRenderMap
				// );

				let notFound = true;
				let htmlVal = props.value;
				if (htmlVal && htmlVal.slice(0, 2) !== "<p") {
					notFound = false;
					htmlVal = "<p>" + htmlVal + "</p>";
				}

				const fromHtml = htmlToDraft(htmlVal, customChunkRenderer);

				if (
					fromHtml &&
					fromHtml.contentBlocks &&
					fromHtml.contentBlocks.length
				) {
					editorState = EditorState.createWithContent(
						ContentState.createFromBlockArray(
							!notFound && fromHtml.contentBlocks.length > 1
								? fromHtml.contentBlocks.slice(
										1,
										fromHtml.contentBlocks.length - 1
								  )
								: fromHtml.contentBlocks,
							fromHtml.entityMap
						),
						decorator
					);
				}
			}
		}

		if (!editorState) {
			editorState = EditorState.createEmpty(decorator);
		}

		this.state = {
			editorState,
			htmlVal: "",
			isOpen: false
		};
	}

	private isRaw(str: string) {
		try {
			return JSON.parse(str) && !!str;
		} catch (e) {
			return false;
		}
	}

	focus = () => {
		this.editor.focus();
	};

	public render() {
		return (
			<Card>
				<div style={{ height: "auto" }}>
					<Editor
						editorState={this.state.editorState}
						wrapperClassName="rich-editor demo-wrapper"
						editorClassName="demo-editor"
						onEditorStateChange={this.onChange}
						placeholder="The message goes here..."
						toolbarCustomButtons={[this.renderToolbar()]}
						keyBindingFn={this.bindKeyCommands}
						handleKeyCommand={this.handleKeyCommand}
						toolbar={{
							options: ["colorPicker", "image"]
						}}
						customBlockRenderFunc={customBlockRenderFunc}
					/>
				</div>
			</Card>
		);
	}

	/**
	 * render editor controls toolbar as a blueprint button group
	 */
	private renderToolbar = () => {
		const { editorState } = this.state;

		const selection = editorState.getSelection();
		const currentBlockType = this.getCurrentBlockType();
		const currentStyle = editorState.getCurrentInlineStyle();
		const currentHasLink = RichUtils.currentBlockContainsLink(editorState);

		const buttonProps: IButtonProps = {
			small: this.props.small,
			large: this.props.large
		};

		const controls: React.ReactNodeArray = [];

		// block type
		controls.push(
			<DropdownSelect
				key="block-type"
				buttonProps={buttonProps}
				onChange={this.toggleBlockType}
				options={blockStyleOptions}
				value={currentBlockType}
			/>
		);

		// block style
		controls.push(
			<ButtonSelect
				key="block-style"
				buttonProps={buttonProps}
				noGroup={true}
				iconOnly={true}
				onChange={this.toggleBlockType}
				options={blockTypeOptions}
				value={currentBlockType}
			/>
		);

		// inline style
		for (const o of inlineStyleOptions) {
			controls.push(
				<Tooltip
					content={o.label}
					key={o.value}
					position={Position.TOP}
				>
					<Button
						{...buttonProps}
						active={currentStyle.has(o.value)}
						icon={o.icon}
						onClick={() => this.toggleInlineStyle(o.value)}
					/>
				</Tooltip>
			);
		}

		// add/change link
		controls.push(
			<Button
				{...buttonProps}
				key="link-add"
				icon="link"
				disabled={
					selection.getEndOffset() - selection.getStartOffset() < 1
				}
				active={currentHasLink}
				onClick={this.addLink}
			/>
		);

		// variables
		if (this.props.argNames && this.props.argNames.length > 0) {
			controls.push(this.renderVariableControls());
		}

		// undo button
		controls.push(
			<Button
				key="undo"
				{...buttonProps}
				icon="undo"
				onClick={this.undo}
				disabled={!editorState.getAllowUndo()}
			/>
		);

		// redo button
		controls.push(
			<Button
				key="redo"
				{...buttonProps}
				icon="redo"
				onClick={this.redo}
				disabled={editorState.getRedoStack().size < 1}
			/>
		);

		// html button
		controls.push(
			<Popover
				key="html-add"
				isOpen={this.state.isOpen}
				content={
					<div
						style={{
							width: "500px"
						}}
					>
						<TextAreaWidget
							value={this.state.htmlVal}
							onChange={this.handleHtmlChange}
						/>
					</div>
				}
			>
				<Tooltip content="Edit as HTML" position={Position.TOP}>
					<Button
						icon={IconNames.CODE}
						active={this.state.isOpen}
						onMouseDown={this.toggleHtmlEditor}
					/>
				</Tooltip>
			</Popover>
		);
		const { minimal = true } = this.props;

		return (
			<FormGroup>
				<ButtonGroup minimal={minimal}>{controls}</ButtonGroup>
			</FormGroup>
		);
	};

	/**
	 * all content-changing methods should call this instead of setState directly
	 * so that it will trigger the debounced onChange.
	 */
	private onChange = (editorState: EditorState) => {
		if (editorState) {
			const d = this.state.editorState.getCurrentContent();
			const a = stateToHTML(this.state.editorState.getCurrentContent());
			this.setState({ editorState }, () => {
				if (!!this.timer) {
					clearTimeout(this.timer);
				}
				this.timer = setTimeout(() => {
					if (IS_DRAFT_JS) {
						let html = draftToHtml(
							convertToRaw(editorState.getCurrentContent()),
							null,
							false,
							entityMapper
						);

						this.props.onChange(html);
						// let html = JSON.stringify(
						// 	convertToRaw(
						// 		this.state.editorState.getCurrentContent()
						// 	)
						// );
						// this.props.onChange(html);
					} else {
						this.props.onChange(
							draftToHtml(
								this.state.editorState.getCurrentContent()
							)
						);
					}
				}, 200);
			});
		}
	};

	private undo = () => {
		const { editorState } = this.state;
		if (editorState.getAllowUndo()) {
			this.onChange(EditorState.undo(editorState));
		}
	};

	private redo = () => {
		const { editorState } = this.state;
		if (editorState.getRedoStack().size > 0) {
			this.onChange(EditorState.undo(this.state.editorState));
		}
	};

	private toggleHtmlEditor = () => {
		if (!this.state.isOpen) {
			let html = draftToHtml(
				convertToRaw(this.state.editorState.getCurrentContent()),
				null,
				false,
				entityMapper
			);

			this.setState({
				isOpen: true,
				htmlVal: html
				// htmlVal: getHtml(this.state.editorState)
			});
		} else {
			let htmlVal = this.state.htmlVal;
			let notFound = true;
			if (htmlVal && htmlVal.slice(0, 2) !== "<p") {
				notFound = false;
				htmlVal = "<p>" + this.state.htmlVal + "</p>";
			}
			const blocksFromHtml = htmlToDraft(htmlVal, customChunkRenderer);
			const { contentBlocks, entityMap } = blocksFromHtml;
			const contentState = ContentState.createFromBlockArray(
				!notFound && contentBlocks.length > 1
					? contentBlocks.slice(1, contentBlocks.length - 1)
					: contentBlocks,
				entityMap
			);

			const editorState = EditorState.createWithContent(contentState);

			this.setState({ isOpen: false });
			this.onChange(editorState);
		}
	};

	private handleHtmlChange = val => {
		{
			this.setState({ htmlVal: val });
		}
	};

	private renderVariableControls = (): JSX.Element => {
		return (
			<Popover
				key="vars-add"
				content={
					<Menu>
						{this.props.argNames.map(name => (
							<MenuItem
								key={name}
								text={name}
								onClick={this.insertVariable(name)}
							/>
						))}
					</Menu>
				}
			>
				<Tooltip
					content="Insert Template Variables"
					position={Position.TOP}
				>
					<Button icon={IconNames.INSERT} />
				</Tooltip>
			</Popover>
		);
	};

	private getCurrentBlockType = (): string => {
		const selection = this.state.editorState.getSelection();
		return this.state.editorState
			.getCurrentContent()
			.getBlockForKey(selection.getStartKey())
			.getType();
	};

	private toggleBlockType = (type: string) => {
		this.onChange(RichUtils.toggleBlockType(this.state.editorState, type));
	};

	private toggleInlineStyle = (inlineStyle: string) => {
		this.onChange(
			RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle)
		);
	};

	private addLink = () => {
		const { editorState } = this.state;
		const selection = editorState.getSelection();
		const url = window.prompt("Enter Link");
		if (!url) {
			this.onChange(RichUtils.toggleLink(editorState, selection, null));
			return;
		}
		const withLink = editorState
			.getCurrentContent()
			.createEntity("LINK", "MUTABLE", {
				url,
				target: "_blank",
				title: editorState
					.getCurrentContent()
					.getBlockForKey(selection.getAnchorKey())
					.getText()
					.slice(selection.getStartOffset(), selection.getEndOffset())
			});
		const nextState = EditorState.push(
			editorState,
			withLink,
			"apply-entity"
		);
		const linkKey = withLink.getLastCreatedEntityKey();
		this.onChange(RichUtils.toggleLink(nextState, selection, linkKey));
		return;
	};

	private handleKeyCommand = (command: string) => {
		if (command === "text-bold") {
			this.toggleInlineStyle("BOLD");
			return "handled";
		}

		if (command === "text-italic") {
			this.toggleInlineStyle("ITALIC");
			return "handled";
		}

		if (command === "text-underline") {
			this.toggleInlineStyle("UNDERLINE");
			return "handled";
		}

		if (command === "link") {
			this.addLink();
			return "handled";
		}

		return "not-handled";
	};

	private bindKeyCommands = (e: KeyboardEvent) => {
		if (KeyBindingUtil.hasCommandModifier(e)) {
			// bold
			if (e.keyCode === 66) {
				return "text-bold";
			}

			// italic
			if (e.keyCode === 73) {
				return "text-italic";
			}

			// underline
			if (e.keyCode === 85) {
				return "text-underline";
			}

			// link
			if (e.keyCode === 75) {
				return "add-link";
			}
		}

		return getDefaultKeyBinding(e);
	};

	private insertVariable = (name: string) => () => {
		const { editorState } = this.state;

		const currentContent = editorState.getCurrentContent();
		const selection = editorState.getSelection();

		this.onChange(
			EditorState.push(
				editorState,
				Modifier.insertText(
					currentContent,
					selection,
					"${" + name + "}",
					null
				),
				"insert-characters"
			)
		);
	};
}



// WEBPACK FOOTER //
// ./src/common/form/widgets/richText/RichTextWidget.tsx